import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, resolveComponent as _resolveComponent, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "app-content" }

import { onMounted, onUnmounted, watch, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import HeroBanner from './components/base/HeroBanner.vue';
import AppHeader from '@/components/base/AppHeader.vue'
import AppFooter from './components/base/AppFooter.vue';
import ToastComponent from '@/components/ToastComponent.vue'
import { useToast } from '@/composables/useToast'
import ThemeToggle from '@/components/ThemeToggle.vue'
import BetslipModal from '@/components/BetslipModal.vue'
import { useModalStore } from '@/stores/modal'
import { useAuthStore } from '@/stores/auth'
import { useGameStore } from '@/stores/game'
import { useUpcomingDrawsStore } from '@/stores/upcomingDraws'
import { getThemeParamFromUrl, initializeTheme, setTheme } from '@/composables/useTheme'
import { resolveThemeName } from '@/utils/themeLoader'
import { iframeCommunication } from '@/utils/iframeCommunication'
import { setApiBaseUrl } from '@/api'
import { useI18n } from 'vue-i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'App',
  setup(__props, { expose: __expose }) {

const { toast, showToast } = useToast()
const modalStore = useModalStore()
const authStore = useAuthStore()
const gameStore = useGameStore()
const upcomingDrawsStore = useUpcomingDrawsStore()
const route = useRoute()
const router = useRouter()
const { t } = useI18n()
const iframeCleanupHandlers: Array<() => void> = []

const isInitialized = ref(false)

const openBetslipModal = () => {
  modalStore.openBetslip()
}

const closeBetslipModal = () => {
  modalStore.closeBetslip()
}

const refreshAccountScopedCatalog = async () => {
  upcomingDrawsStore.fetchDraws(1)
  gameStore.clearProducts()
}

// Watch for account changes in URL
watch(
  () => route.query.account,
  async (newAccount, oldAccount) => {
    if (newAccount !== oldAccount && newAccount && typeof newAccount === 'string') {
      authStore.account = newAccount
      sessionStorage.setItem('auth_account', newAccount)
      
      try {
        await initializeTheme(true)
        const currency = sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
      } catch (error) {
        console.error('Failed to re-initialize theme:', error)
      }
    }
  },
  { immediate: false }
)

// Only apply game-settings theme when URL has no theme param (URL wins)
watch(
  () => gameStore.getBetSettings?.theme,
  async (newTheme) => {
    if (newTheme && !getThemeParamFromUrl()) {
      try {
        await setTheme(resolveThemeName(newTheme))
      } catch (error) {
        console.error('Failed to update theme from game settings:', error)
      }
    }
  },
  { immediate: false }
)

const isInIframe = iframeCommunication.isRunningInIframe()

onMounted(async () => {
  const handleParentLogout = () => {
    authStore.logout({ silent: true, reason: 'host-request' })
    modalStore.closeBetslip()
    showToast({
      title: t('common.loggedOut'),
      message: t('common.sessionClosed'),
      type: 'info',
      duration: 4000
    })
    router.replace({ path: '/next-draws', query: {} })
  }

  const handleParentNavigate = (data?: Record<string, unknown>) => {
    try {
      if (!data) return
      const target = data.route || data.path || data.name
      if (!target || typeof target !== 'string') return
      
      const query = (data.query && typeof data.query === 'object') 
        ? data.query as Record<string, string> 
        : undefined

      if (target.startsWith('/')) {
        router.push({ path: target, query })
      } else {
        router.push({ name: target, query })
      }
    } catch (e) {
      console.error('Iframe navigation failed:', e)
    }
  }

  // CORE HANDSHAKE HANDLER: This is where configuration is synchronized
  const handleParentReady = async (data?: Record<string, unknown>) => {
    console.log('[App] Handshake received from parent:', data)
    if (data?.config && typeof data.config === 'object') {
      const config = data.config as Record<string, any>
      
      // 1. Synchronize Authentication/Account state
      const accountStr =
        typeof config.account === 'string'
          ? config.account.trim()
          : config.account != null
            ? String(config.account).trim()
            : ''
      if (accountStr) {
        authStore.account = accountStr
        sessionStorage.setItem('auth_account', accountStr)
      }
      const tokenStr =
        typeof config.token === 'string'
          ? config.token.trim()
          : config.token != null
            ? String(config.token).trim()
            : ''
      if (tokenStr) {
        authStore.token = tokenStr
        sessionStorage.setItem('auth_token', tokenStr)
      }
      if (config.currency) {
        sessionStorage.setItem('auth_currency', config.currency)
      }

      // 2. Apply dynamic API base URL
      if (config.GAME_SERVER_URL) {
        setApiBaseUrl(config.GAME_SERVER_URL)
      }

      // 3. Trigger initial settings fetch with correct URL/Headers
      try {
        await initializeTheme(true)
        const currency = config.currency || sessionStorage.getItem('auth_currency')
        if (currency) {
          await gameStore.fetchGameSettings(false)
        }
        await refreshAccountScopedCatalog()
      } catch (e) {
        console.warn('Initialization after handshake failed:', e)
      }
    } else {
      // No config provided by parent, just initialize theme
      await initializeTheme()
    }
    
    // 4. Reveal the app content
    isInitialized.value = true
  }

  // Register communication listeners
  iframeCleanupHandlers.push(iframeCommunication.on('logout', handleParentLogout))
  iframeCleanupHandlers.push(iframeCommunication.on('navigate', handleParentNavigate))
  iframeCleanupHandlers.push(iframeCommunication.on('parent-ready', handleParentReady))

  if (isInIframe) {
    document.body.classList.add('iframe-mode')
    document.documentElement.classList.add('iframe-mode')
    
    // Measure and report height
    iframeCommunication.startObserving('iframe-root')
    
    // START HANDSHAKE: Tell parent child is ready to receive configuration
    iframeCommunication.sendReady()

    // Safety timeout: If handshake doesn't complete in 4s, proceed with default init
    setTimeout(async () => {
      if (!isInitialized.value) {
        console.warn('[App] Handshake timeout - proceeding with default initialization')
        await initializeTheme()
        isInitialized.value = true
      }
    }, 4000)

    // Handle deep links from URL params
    const gameSlug = (route.query.game || route.query.gameSlug) as string | undefined
    if (gameSlug && typeof gameSlug === 'string' && gameSlug.trim()) {
      const slug = gameSlug.trim()
      const restQuery = { ...route.query }
      delete restQuery.game
      delete restQuery.gameSlug
      router.replace({ path: `/lottery/${slug}`, query: restQuery as Record<string, string> })
    }
  } else {
    // Standard standalone mode
    const rawAccount =
      authStore.account ||
      sessionStorage.getItem('auth_account') ||
      (route.query.account as string | undefined)
    const account =
      typeof rawAccount === 'string'
        ? rawAccount.trim()
        : rawAccount != null
          ? String(rawAccount).trim()
          : ''
    if (account) {
      authStore.account = account
      sessionStorage.setItem('auth_account', account)
    }

    const rawToken = route.query.token as string | undefined
    const token =
      typeof rawToken === 'string' ? rawToken.trim() : rawToken != null ? String(rawToken).trim() : ''
    if (token) {
      authStore.token = token
      sessionStorage.setItem('auth_token', token)
    }

    try {
      await initializeTheme()
      const currency = sessionStorage.getItem('auth_currency')
      if (account && currency) {
        await gameStore.fetchGameSettings(false)
      }
    } catch (e) {
      console.warn('Failed to load initial settings:', e)
    }
    
    isInitialized.value = true
  }
})

onUnmounted(() => {
  iframeCommunication.destroy()
  iframeCleanupHandlers.forEach((cleanup) => cleanup())
})

__expose({
  openBetslipModal,
  closeBetslipModal
})

return (_ctx: any,_cache: any) => {
  const _component_router_view = _resolveComponent("router-view")!

  return (_openBlock(), _createElementBlock("div", {
    id: "iframe-root",
    class: _normalizeClass({ 'is-iframe': _unref(isInIframe) })
  }, [
    _createVNode(HeroBanner),
    _createVNode(AppHeader),
    _createElementVNode("div", _hoisted_1, [
      _createVNode(_component_router_view, { onOpenBetslipModal: openBetslipModal }),
      _createVNode(AppFooter),
      _createVNode(ThemeToggle, { class: "desktop-only" }),
      (_unref(toast))
        ? (_openBlock(), _createBlock(ToastComponent, {
            key: 0,
            title: _unref(toast).title,
            message: _unref(toast).message,
            type: _unref(toast).type,
            duration: _unref(toast).duration
          }, null, 8, ["title", "message", "type", "duration"]))
        : _createCommentVNode("", true),
      _createVNode(BetslipModal, {
        "is-open": _unref(modalStore).showBetslip,
        onClose: closeBetslipModal
      }, null, 8, ["is-open"])
    ])
  ], 2))
}
}

})